import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Table } from 'react-bootstrap';

/**
 * TeamPerformance displays a styled Bootstrap table with one row per BTT user,
 * dynamically generated month columns, and a total column.
 */

const SHORT_MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Formats a month key like "2025-01" or "202501" into "Jan 25".
 */
const formatMonthHeader = (monthKey) => {
  let year, monthNum;

  if (monthKey.includes('-')) {
    const parts = monthKey.split('-');
    year = parts[0];
    monthNum = parseInt(parts[1], 10);
  } else if (monthKey.length === 6) {
    year = monthKey.slice(0, 4);
    monthNum = parseInt(monthKey.slice(4), 10);
  } else {
    return monthKey;
  }

  const monthIndex = monthNum - 1;
  if (monthIndex >= 0 && monthIndex < 12) {
    return `${SHORT_MONTH_NAMES[monthIndex]} '${year.slice(2)}`;
  }
  return monthKey;
};

const TeamPerformance = ({ data = [] }) => {
  const allMonths = useMemo(() => {
    if (!data || data.length === 0) return [];
    const monthSet = new Set();
    data.forEach((entry) => {
      if (entry.months) {
        Object.keys(entry.months).forEach((month) => monthSet.add(month));
      }
    });
    return Array.from(monthSet).sort((a, b) => a.localeCompare(b));
  }, [data]);

  if (!data || data.length === 0) {
    return <p className="text-center text-muted">No data available</p>;
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table hover size="sm" className="mb-0 align-middle">
        <thead>
          <tr style={{ backgroundColor: '#f8f9fc' }}>
            <th
              style={{
                position: 'sticky',
                left: 0,
                backgroundColor: '#f8f9fc',
                zIndex: 1,
                minWidth: '180px',
                fontWeight: 600,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                color: '#5a5c69',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #e3e6f0',
                padding: '10px 12px',
              }}
            >
              User
            </th>
            {allMonths.map((month) => (
              <th
                key={month}
                style={{
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  textTransform: 'uppercase',
                  color: '#5a5c69',
                  letterSpacing: '0.5px',
                  borderBottom: '2px solid #e3e6f0',
                  padding: '10px 8px',
                  minWidth: '70px',
                  whiteSpace: 'nowrap',
                }}
              >
                {formatMonthHeader(month)}
              </th>
            ))}
            <th
              style={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '0.8rem',
                textTransform: 'uppercase',
                color: '#4e73df',
                letterSpacing: '0.5px',
                borderBottom: '2px solid #4e73df',
                padding: '10px 8px',
                minWidth: '70px',
              }}
            >
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((entry, index) => (
            <tr
              key={entry.user}
              style={{
                backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fc',
              }}
            >
              <td
                style={{
                  position: 'sticky',
                  left: 0,
                  backgroundColor: index % 2 === 0 ? '#fff' : '#f8f9fc',
                  zIndex: 1,
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  padding: '8px 12px',
                  borderBottom: '1px solid #e3e6f0',
                }}
              >
                {entry.user}
              </td>
              {allMonths.map((month) => {
                const value = entry.months?.[month] || 0;
                return (
                  <td
                    key={month}
                    style={{
                      textAlign: 'center',
                      fontSize: '0.85rem',
                      padding: '8px',
                      borderBottom: '1px solid #e3e6f0',
                      color: value === 0 ? '#ccc' : '#333',
                      fontWeight: value > 0 ? 500 : 400,
                    }}
                  >
                    {value}
                  </td>
                );
              })}
              <td
                style={{
                  textAlign: 'center',
                  fontWeight: 700,
                  fontSize: '0.85rem',
                  padding: '8px',
                  borderBottom: '1px solid #e3e6f0',
                  color: '#4e73df',
                }}
              >
                {entry.total}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

TeamPerformance.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      user: PropTypes.string.isRequired,
      months: PropTypes.objectOf(PropTypes.number).isRequired,
      total: PropTypes.number.isRequired,
    })
  ),
};

export default TeamPerformance;
